import React from 'react';
import { Modal, Box } from '@mui/material';
import styled from 'styled-components';

const ModalContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(360px, 90%);
  background: #fff;
  border-radius: 20px;
  padding: clamp(24px, 4vw, 32px) clamp(20px, 4vw, 28px);
  text-align: center;
  font-family: 'Urbanist', -apple-system, BlinkMacSystemFont, sans-serif;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  position: relative;
  outline: none;

  @media (max-width: 480px) {
    width: 95%;
    padding: 24px 20px;
    border-radius: 16px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: #999999;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: #666666;
  }
  
  @media (max-width: 480px) {
    top: 12px;
    right: 12px;
    padding: 6px;
  }
`;

const Title = styled.h2`
  font-size: clamp(20px, 3vw, 24px);
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 clamp(12px, 2vw, 16px) 0;
  font-family: 'Urbanist', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.3;
  
  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 12px;
  }
`;

const Description = styled.p`
  font-size: clamp(14px, 2vw, 16px);
  color: #666666;
  margin: 0 0 clamp(28px, 4vw, 36px) 0;
  font-family: 'Urbanist', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.5;
  
  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 24px;
  }
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background: linear-gradient(135deg, #12188A 0%, #0e146b 100%);
  color: white;
  border: none;
  border-radius: 25px;
  font-family: 'Urbanist', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  box-shadow: 0 4px 16px rgba(18, 24, 138, 0.3);
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(18, 24, 138, 0.4);
    background: linear-gradient(135deg, #0e146b 0%, #0a1052 100%);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 4px 16px rgba(18, 24, 138, 0.2);
  }
  
  @media (max-width: 480px) {
    max-width: 100%;
    padding: 10px 20px;
    font-size: 15px;
    min-height: 40px;
  }
`;

const LoaderSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @media (max-width: 480px) {
    width: 16px;
    height: 16px;
    margin-right: 6px;
  }
`;

const LogoutModal = ({ 
  open, 
  onClose, 
  onLogout, 
  isLoggingOut = false 
}) => {
  return (
    <Modal
      open={open}
      onClose={!isLoggingOut ? onClose : undefined}
      aria-labelledby="logout-modal-title"
      aria-describedby="logout-modal-description"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.4)'
        }
      }}
    >
      <ModalContainer>
        <CloseButton onClick={onClose} disabled={isLoggingOut}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </CloseButton>

        <Title id="logout-modal-title">
          Logout?
        </Title>

        <Description id="logout-modal-description">
          Are you sure you want to logout?
        </Description>

        <LogoutButton
          onClick={onLogout}
          disabled={isLoggingOut}
        >
          {isLoggingOut && <LoaderSpinner />}
          Logout
        </LogoutButton>
      </ModalContainer>
    </Modal>
  );
};

export default LogoutModal;